import React, { useMemo } from "react";
import ModalShell from "./ModalShell";
import VisibilityReorderPicker from "./VisibilityReorderPicker";
import "./ColumnCustomizerModal.css";

/**
 * Generic modal to customize visible columns for any table.
 * Uses VisibilityReorderPicker (checkbox stack + drag handles) — mobile-friendly.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - allColumns: Array<{ id: string, label: string, fixed?: boolean }>
 * - visibleIds: string[] (currently visible column ids)
 * - onChangeVisibleIds: (ids: string[]) => void
 * - onSave: (ids: string[]) => Promise<void> | void
 * - saving: boolean
 * - onReset?: () => void
 */
export default function ColumnCustomizerModal({
  open,
  onClose,
  allColumns,
  visibleIds,
  onChangeVisibleIds,
  onSave,
  saving,
  onReset,
}) {
  const columns = useMemo(
    () => (allColumns || []).filter((column) => column && column.id),
    [allColumns]
  );

  const visibleOrdered = useMemo(() => {
    const byId = new Map(columns.map((column) => [column.id, column]));
    return (visibleIds || []).map((id) => byId.get(id)).filter(Boolean);
  }, [columns, visibleIds]);

  const handleSaveClick = async () => {
    const ids = visibleOrdered.map((column) => column.id);
    if (!ids.length) return;
    if (typeof onSave === "function") {
      await onSave(ids);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Customize columns"
      subtitle="Toggle columns on or off and drag to reorder. Only column visibility and order are saved for this table."
      maxWidth={520}
      zIndex={1100}
      className="column-customizer-modal"
      overlayClassName="column-customizer-modal-overlay"
      bodyClassName="column-customizer-modal__body"
      footerClassName="column-customizer-modal__footer"
      footer={
        <div className="column-customizer-footer">
          <p className="column-customizer-footer__help" role="note">
            <strong>Save</strong> = columns only · <strong>Reset</strong> = defaults ·{" "}
            <strong>Cancel</strong> = discard
          </p>
          <div className="column-customizer-footer-actions">
            {typeof onReset === "function" ? (
              <button
                type="button"
                className="btn btn-sm btn-link column-customizer-footer-actions__reset"
                onClick={onReset}
                title="Restore the default columns for this table and save them"
              >
                Reset
              </button>
            ) : (
              <span className="column-customizer-footer-actions__spacer" aria-hidden="true" />
            )}
            <div className="column-customizer-footer-actions__end">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={onClose}
                title="Close without saving column changes"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSaveClick}
                disabled={saving || visibleOrdered.length === 0}
                title="Save which columns are visible and their order for this table"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      }
    >
      <VisibilityReorderPicker
        items={columns}
        selectedIds={visibleIds}
        onChangeSelectedIds={onChangeVisibleIds}
        minSelected={1}
        enableReorder
        searchPlaceholder="Search columns…"
        emptyMessage="No columns match your search."
      />
    </ModalShell>
  );
}
